import { protoRoot } from './schema';

const FromRadioType = protoRoot.lookupType('meshtastic.FromRadio');
const ToRadioType = protoRoot.lookupType('meshtastic.ToRadio');
const RouteDiscoveryType = protoRoot.lookupType('meshtastic.RouteDiscovery');
const UserType = protoRoot.lookupType('meshtastic.User');
const PositionType = protoRoot.lookupType('meshtastic.Position');

export const HANDSHAKE_CONFIG_NONCE = 69420;
export const HANDSHAKE_NODEDB_NONCE = 69421;

export interface DecodedData {
  portnum: number;
  payload: Uint8Array;
  wantResponse?: boolean;
  dest?: number;
  source?: number;
  requestId?: number;
  replyId?: number;
  emoji?: number;
  bitfield?: number;
}

export interface DecodedMeshPacket {
  from: number;
  to: number;
  channel?: number;
  decoded?: DecodedData;
  encrypted?: Uint8Array;
  id: number;
  rxTime?: number;
  rxSnr?: number;
  hopLimit?: number;
  wantAck?: boolean;
  priority?: number;
  rxRssi?: number;
  delayed?: number;
  viaMqtt?: boolean;
  hopStart?: number;
  publicKey?: Uint8Array;
  pkiEncrypted?: boolean;
  relayNode?: number;
}

export interface DecodedUser {
  id?: string;
  longName?: string;
  shortName?: string;
  macaddr?: Uint8Array;
  hwModel?: number;
  isLicensed?: boolean;
  role?: number;
  publicKey?: Uint8Array;
}

export interface DecodedPosition {
  latitudeI?: number;
  longitudeI?: number;
  latitude_i?: number;
  longitude_i?: number;
  latitude?: number;
  longitude?: number;
  altitude?: number;
  time?: number;
  locationSource?: number;
  altitudeSource?: number;
  timestamp?: number;
  timestampMillisAdjust?: number;
  altitudeHae?: number;
  altitudeGeoidalSeparation?: number;
  pdop?: number;
  hdop?: number;
  vdop?: number;
  gpsAccuracy?: number;
  groundSpeed?: number;
  groundTrack?: number;
  fixQuality?: number;
  fixType?: number;
  satsInView?: number;
  sensorId?: number;
  nextUpdate?: number;
  seqNumber?: number;
  precisionBits?: number;
}

export interface DecodedNodeInfo {
  num: number;
  user?: DecodedUser;
  position?: DecodedPosition;
  snr?: number;
  lastHeard?: number;
  channel?: number;
  viaMqtt?: boolean;
  hopsAway?: number;
  isFavorite?: boolean;
  isIgnored?: boolean;
}

export interface DecodedMyNodeInfo {
  myNodeNum: number;
  hasGps?: boolean;
  numBands?: number;
  firmwareVersion?: string;
  rebootCount?: number;
  bitrate?: number;
  messageTimeoutMsec?: number;
  minAppVersion?: number;
  maxChannels?: number;
  hasWifi?: boolean;
  channelUtilization?: number;
  airUtilTx?: number;
}

export interface DecodedFileInfo {
  fileName?: string;
  sizeBytes?: number;
}

export interface DecodedFromRadio {
  id?: number;
  packet?: DecodedMeshPacket;
  myInfo?: DecodedMyNodeInfo;
  nodeInfo?: DecodedNodeInfo;
  fileInfo?: DecodedFileInfo;
  configCompleteId?: number;
  rebooted?: boolean;
}

export interface DecodedRouteDiscovery {
  route?: number[];
  snrTowards?: number[];
  routeBack?: number[];
  snrBack?: number[];
}

/**
 * Decodes a raw byte buffer received from a Meshtastic device into a structured FromRadio message.
 *
 * @param bytes - Protobuf byte buffer emitted by Serial framing or BLE characteristic
 * @returns Decoded FromRadio message object
 */
export function decodeFromRadio(bytes: Uint8Array): DecodedFromRadio {
  const message = FromRadioType.decode(bytes);
  return FromRadioType.toObject(message, {
    defaults: false,
    arrays: true,
    objects: true,
    bytes: Uint8Array
  }) as DecodedFromRadio;
}

/**
 * Encodes a ToRadio command into serialized Protobuf bytes ready for transmission.
 *
 * @param payload - Plain object matching ToRadio schema
 * @returns Serialized Protobuf byte buffer
 */
export function encodeToRadio(payload: Record<string, unknown>): Uint8Array {
  const errMsg = ToRadioType.verify(payload);
  if (errMsg) {
    throw new Error(`Invalid ToRadio payload: ${errMsg}`);
  }
  const message = ToRadioType.create(payload);
  return ToRadioType.encode(message).finish();
}

/**
 * Decodes RouteDiscovery payload carried inside a TRACEROUTE_APP packet.
 *
 * @param payload - Raw bytes from MeshPacket decoded payload
 * @returns Decoded route hops and SNR values
 */
export function decodeRouteDiscovery(payload: Uint8Array): DecodedRouteDiscovery {
  const message = RouteDiscoveryType.decode(payload);
  return RouteDiscoveryType.toObject(message, {
    defaults: true,
    arrays: true
  }) as DecodedRouteDiscovery;
}

/**
 * Formats a 32-bit unsigned Meshtastic node integer into standard hex representation (!xxxxxxxx).
 *
 * @param nodeId - Unsigned 32-bit node ID
 * @returns Formatted hex string prefixed with exclamation mark
 */
export function formatNodeHex(nodeId: number): string {
  // Use unsigned right shift to guarantee 32-bit unsigned representation
  const unsigned = nodeId >>> 0;
  return `!${unsigned.toString(16).padStart(8, '0')}`;
}

/**
 * Parses a hex node string (!xxxxxxxx or xxxxxxxx) into a 32-bit unsigned integer.
 *
 * @param hexStr - Hex string with or without leading exclamation mark
 * @returns Unsigned 32-bit integer
 */
export function parseNodeHex(hexStr: string): number {
  const clean = hexStr.startsWith('!') ? hexStr.slice(1) : hexStr;
  return parseInt(clean, 16) >>> 0;
}

/**
 * Calculates the 8-bit hash of a 32-bit node ID for matching with relay_node (last hop).
 *
 * @param nodeId - Unsigned 32-bit node ID
 * @returns 8-bit integer (0..255)
 */
export function getNodeByteHash(nodeId: number): number {
  // Meshtastic compact routing uses the lowest byte (nodeId & 0xFF) as an 8-bit node identifier in packet headers
  return (nodeId >>> 0) & 0xFF;
}

/**
 * Creates a ToRadio packet requesting the radio node configuration and NodeDB.
 *
 * @param configId - Unique config request session ID (default 1)
 * @returns Serialized ToRadio byte buffer
 */
export function createWantConfigRequest(configId = 1): Uint8Array {
  return encodeToRadio({
    wantConfigId: configId
  });
}

/**
 * Builds a ToRadio packet carrying a TRACEROUTE_APP packet targeted to a specific destination node.
 *
 * @param toNode - Destination node 32-bit integer (or 0xFFFFFFFF for broadcast)
 * @param fromNode - Local node 32-bit integer
 * @param packetId - Unique 32-bit packet identifier
 * @param hopLimit - Maximum hop limit for the traceroute (default 3)
 * @returns Serialized ToRadio byte buffer
 */
export function createTraceroutePacket(
  toNode: number,
  fromNode: number,
  packetId: number,
  hopLimit = 3
): Uint8Array {
  // Empty RouteDiscovery payload initializes traceroute tracking for intermediate nodes to append their IDs
  const emptyRoutePayload = RouteDiscoveryType.encode(
    RouteDiscoveryType.create({ route: [], snrTowards: [] })
  ).finish();

  return encodeToRadio({
    packet: {
      from: fromNode,
      to: toNode,
      id: packetId,
      wantAck: true,
      hopLimit,
      hopStart: hopLimit,
      decoded: {
        portnum: 70, // TRACEROUTE_APP = 70
        payload: emptyRoutePayload,
        wantResponse: true
      }
    }
  });
}

/**
 * Decodes a User protobuf payload carried inside a NODEINFO_APP packet.
 *
 * @param payload - Raw bytes from decoded data payload
 * @returns Decoded User object
 */
export function decodeUser(payload: Uint8Array): DecodedUser {
  const message = UserType.decode(payload);
  return UserType.toObject(message, {
    defaults: false,
    bytes: Uint8Array
  }) as DecodedUser;
}

/**
 * Decodes a Position protobuf payload carried inside a POSITION_APP packet.
 *
 * @param payload - Raw bytes from decoded data payload
 * @returns Decoded Position object
 */
export function decodePosition(payload: Uint8Array): DecodedPosition {
  const message = PositionType.decode(payload);
  return PositionType.toObject(message, {
    defaults: false
  }) as DecodedPosition;
}

/**
 * Encodes a ToRadio Heartbeat command with the specified nonce to keep the radio connection alive
 * and advance the handshake state machine.
 *
 * @param nonce - Incremented nonce integer
 * @returns Serialized ToRadio byte buffer
 */
export function createHeartbeat(nonce: number): Uint8Array {
  return encodeToRadio({
    heartbeat: { nonce }
  });
}

